import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Spinner } from "react-bootstrap";
import {
  ArrowRight,
  StarFill,
  HandThumbsUpFill,
  ExclamationTriangleFill,
  ChatSquareQuoteFill,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

const FeedbackInsight = () => {
  const navigate = useNavigate();

  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFeedbackInsight();
  }, []);

  const fetchFeedbackInsight = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://apisay.xpansecoffee.com/api/analytics/kpis",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = await response.json();

      if (result?.status) {
        setInsight(result.data);
      } else {
        setError(result?.message || "Unable to load feedback insights.");
      }
    } catch (error) {
      console.error("Feedback insight fetch error:", error);
      setError("Unable to load feedback insights.");
    } finally {
      setLoading(false);
    }
  };

  const summary = insight?.summary || {};
  const averages = summary?.average_scores || {};
  const recommendation = insight?.recommendation || {};

  /*
   * total_feedbacks is currently null in the API.
   * Calculate it from overall experience distribution.
   */
  const totalFeedbacks = useMemo(() => {
    if (
      summary?.total_feedbacks !== null &&
      summary?.total_feedbacks !== undefined
    ) {
      return Number(summary.total_feedbacks || 0);
    }

    const distribution = insight?.score_distributions?.overall_experience || {};

    return Object.values(distribution).reduce(
      (total, count) => total + Number(count || 0),
      0,
    );
  }, [summary?.total_feedbacks, insight?.score_distributions]);

  /*
   * Calculate recommendation percentage because
   * likely_to_recommend_percent currently comes as 0.
   */
  const recommendationPercent = useMemo(() => {
    if (!totalFeedbacks) return 0;

    return (
      (Number(recommendation?.likely_to_recommend || 0) / totalFeedbacks) * 100
    );
  }, [recommendation?.likely_to_recommend, totalFeedbacks]);

  const overallRating = Number(averages?.overall_experience || 0);

  const valueForMoney = Number(averages?.menu_value_for_money || 0);

  return (
    <Container fluid className="p-0">
      <Card className="border-0 feedback-insight-card">
        <Card.Body className="feedback-insight-body">
          {loading ? (
            <div className="feedback-loading">
              <Spinner
                animation="border"
                size="sm"
                className="feedback-spinner"
              />

              <span>Loading feedback insights...</span>
            </div>
          ) : error ? (
            <div className="feedback-error">
              <div className="feedback-error-title">
                Feedback insights unavailable
              </div>

              <div className="feedback-error-text">{error}</div>

              <button
                type="button"
                onClick={fetchFeedbackInsight}
                className="feedback-retry-button"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="feedback-main-grid">
              {/* LEFT CONTENT */}
              <div className="feedback-left">
                <div className="feedback-badge">Customer Feedback</div>

                <h2 className="feedback-title">
                  How are our <span>customers feeling?</span>
                </h2>

                <p className="feedback-description">
                  A quick overview of customer satisfaction and service quality.
                </p>

                <button
                  type="button"
                  className="feedback-details-button"
                  onClick={() => navigate("/smart-insights/feedback-listing")}
                >
                  View Details
                  <ArrowRight size={19} />
                </button>
              </div>

              {/* CENTER ILLUSTRATION */}
              <div className="feedback-illustration">
                <div className="feedback-illustration-circle">
                  <div className="feedback-decoration decoration-one" />
                  <div className="feedback-decoration decoration-two" />

                  <div className="feedback-spark spark-one" />
                  <div className="feedback-spark spark-two" />
                  <div className="feedback-spark spark-three" />

                  <div className="feedback-chat-card">
                    <div className="feedback-stars">
                      <StarFill />
                      <StarFill />
                      <StarFill />
                      <StarFill />
                      <StarFill />
                    </div>

                    <div className="feedback-smile">
                      <span />
                      <span />
                      <div />
                    </div>

                    <ChatSquareQuoteFill className="feedback-quote-icon" />
                  </div>
                </div>
              </div>

              {/* RIGHT STATS */}
              <div className="feedback-stats">
                {/* OVERALL RATING */}
                <div className="feedback-stat feedback-stat-rating">
                  <div className="feedback-stat-icon rating-icon">
                    <StarFill />
                  </div>

                  <div className="feedback-stat-value">
                    {overallRating.toFixed(1)}
                    <span>/ 5</span>
                  </div>

                  <div className="feedback-stat-label">Overall Rating</div>
                </div>

                {/* WOULD RECOMMEND */}
                <div className="feedback-stat feedback-stat-recommend">
                  <div className="feedback-stat-icon recommend-icon">
                    <HandThumbsUpFill />
                  </div>

                  <div className="feedback-stat-value">
                    {Math.round(recommendationPercent)}%
                  </div>

                  <div className="feedback-stat-label">Would Recommend</div>
                </div>

                {/* VALUE FOR MONEY */}
                <div className="feedback-stat feedback-stat-attention">
                  <div className="feedback-stat-icon attention-icon">
                    <ExclamationTriangleFill />
                  </div>

                  <div className="feedback-stat-value">
                    {valueForMoney.toFixed(1)}
                    <span>/ 5</span>
                  </div>

                  <div className="feedback-stat-label">
                    Value for Money
                    <small>Needs Attention</small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <style>
        {`

                /* =====================================
                   MAIN CARD
                ===================================== */

                .feedback-insight-card {
                    border-radius: 24px !important;
                    background:
                        linear-gradient(
                            135deg,
                            #F4FAFF 0%,
                            #EDF7FF 55%,
                            #EAF5FF 100%
                        ) !important;
                    box-shadow: none !important;
                    overflow: hidden;
                    border: 1px solid #DDEEFF !important;
                }

                .feedback-insight-body {
                    padding: 30px 34px 30px 42px;
                }

                .feedback-main-grid {
                    display: grid;
                    grid-template-columns:
                        1.05fr 0.65fr 1.35fr;
                    align-items: center;
                    gap: 30px;
                    min-height: 290px;
                }


                /* =====================================
                   LEFT CONTENT
                ===================================== */

                .feedback-badge {
                    display: inline-flex;
                    align-items: center;

                    padding: 7px 18px;

                    background: #DCEEFF;

                    border: 1px solid #C9E4FF;
                    border-radius: 14px;

                    font-size: 14px;
                    font-weight: 600;

                    color: #1677E8;

                    margin-bottom: 22px;
                }

                .feedback-title {
                    font-size: 38px;
                    line-height: 1.2;

                    font-weight: 600;

                    color: #071C3A;

                    margin: 0 0 14px;

                    max-width: 460px;
                }

                .feedback-title span {
                    color: #1677E8;
                }

                .feedback-description {
                    font-size: 12px;
                    line-height: 1.6;

                    color: #60748A;

                    max-width: 390px;

                    margin: 0 0 24px;
                }

                .feedback-details-button {
                    border: 1.5px solid #123B67;

                    background: rgba(
                        255,
                        255,
                        255,
                        0.35
                    );

                    color: #102A43;

                    border-radius: 10px;

                    padding: 11px 17px;

                    font-weight: 700;
                    font-size: 14px;

                    display: inline-flex;
                    align-items: center;

                    gap: 10px;

                    cursor: pointer;

                    transition:
                        background 0.2s ease,
                        color 0.2s ease,
                        border-color 0.2s ease,
                        transform 0.2s ease;
                }

                .feedback-details-button:hover {
                    background: #1677E8;
                    border-color: #1677E8;
                    color: #FFFFFF;
                    transform: translateY(-1px);
                }


                /* =====================================
                   ILLUSTRATION
                ===================================== */

                .feedback-illustration {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .feedback-illustration-circle {
                    width: 178px;
                    height: 178px;

                    border-radius: 50%;

                    background:
                        linear-gradient(
                            145deg,
                            #C9E5FF,
                            #E5F3FF
                        );

                    position: relative;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }


                /* Decorative soft circles */

                .feedback-decoration {
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(
                        86,
                        166,
                        241,
                        0.15
                    );
                }

                .decoration-one {
                    width: 38px;
                    height: 38px;

                    left: 10px;
                    top: 22px;
                }

                .decoration-two {
                    width: 25px;
                    height: 25px;

                    right: 13px;
                    bottom: 20px;
                }


                /* Chat bubble */

                .feedback-chat-card {
                    width: 132px;
                    height: 103px;

                    background: #FFFFFF;

                    border-radius: 17px;

                    box-shadow:
                        0 14px 30px
                        rgba(37, 112, 180, 0.14);

                    position: relative;

                    display: flex;
                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    transform: rotate(-5deg);
                }

                .feedback-chat-card::after {
                    content: "";

                    position: absolute;

                    bottom: -12px;
                    left: 30px;

                    width: 25px;
                    height: 25px;

                    background: #FFFFFF;

                    clip-path: polygon(
                        0 0,
                        100% 0,
                        0 100%
                    );
                }


                /* Stars */

                .feedback-stars {
                    display: flex;

                    gap: 4px;

                    color: #FFB51B;

                    font-size: 15px;

                    margin-bottom: 13px;
                }


                /* Quote icon */

                .feedback-quote-icon {
                    position: absolute;

                    right: 11px;
                    bottom: 11px;

                    font-size: 15px;

                    color: #A7D2F8;
                }


                /* Smile */

                .feedback-smile {
                    width: 45px;
                    height: 25px;

                    position: relative;
                }

                .feedback-smile > span {
                    position: absolute;

                    top: 0;

                    width: 5px;
                    height: 5px;

                    background: #4C9BE8;

                    border-radius: 50%;
                }

                .feedback-smile > span:first-child {
                    left: 8px;
                }

                .feedback-smile > span:nth-child(2) {
                    right: 8px;
                }

                .feedback-smile > div {
                    position: absolute;

                    left: 50%;
                    bottom: 1px;

                    transform: translateX(-50%);

                    width: 27px;
                    height: 13px;

                    border-bottom:
                        3px solid #4C9BE8;

                    border-radius:
                        0 0 20px 20px;
                }


                /* Spark marks */

                .feedback-spark {
                    position: absolute;

                    background: #2689E8;

                    border-radius: 5px;
                }

                .spark-one {
                    width: 5px;
                    height: 19px;

                    top: 16px;
                    right: 18px;

                    transform: rotate(28deg);
                }

                .spark-two {
                    width: 5px;
                    height: 16px;

                    top: 34px;
                    right: 5px;

                    transform: rotate(65deg);
                }

                .spark-three {
                    width: 16px;
                    height: 5px;

                    top: 9px;
                    right: 39px;

                    transform: rotate(7deg);
                }


                /* =====================================
                   RIGHT STAT CONTAINER
                ===================================== */

                .feedback-stats {
                    background:
                        rgba(255, 255, 255, 0.68);

                    border: 1px solid
                        rgba(207, 229, 249, 0.8);

                    border-radius: 18px;

                    padding: 12px;

                    display: flex;
                    flex-direction: column;

                    gap: 9px;
                }


                /* =====================================
                   INDIVIDUAL STATS
                ===================================== */

                .feedback-stat {
                    min-height: 72px;

                    border-radius: 13px;

                    display: grid;

                    grid-template-columns:
                        48px 95px 1fr;

                    align-items: center;

                    padding: 10px 16px;

                    gap: 8px;
                }


                /* Rating */

                .feedback-stat-rating {
                    background:
                        linear-gradient(
                            90deg,
                            #FFF9E7,
                            #FFFCF1
                        );
                }

                .rating-icon {
                    color: #F4AD12;
                    background: #FFF4CC;
                }


                /* Recommend */

                .feedback-stat-recommend {
                    background:
                        linear-gradient(
                            90deg,
                            #EAFBF4,
                            #F2FCF8
                        );
                }

                .recommend-icon {
                    color: #08A878;
                    background: #D8F6EA;
                }


                /* Attention */

                .feedback-stat-attention {
                    background:
                        linear-gradient(
                            90deg,
                            #FFF1EA,
                            #FFF7F2
                        );
                }

                .attention-icon {
                    color: #F25B2A;
                    background: #FFE0D2;
                }


                /* Stat icons */

                .feedback-stat-icon {
                    width: 40px;
                    height: 40px;

                    border-radius: 50%;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    font-size: 19px;
                }


                /* Value */

                .feedback-stat-value {
                    font-size: 27px;

                    line-height: 1;

                    font-weight: 800;

                    color: #071C3A;

                    white-space: nowrap;
                }

                .feedback-stat-value span {
                    font-size: 16px;

                    font-weight: 600;

                    color: #71869A;

                    margin-left: 2px;
                }


                /* Label */

                .feedback-stat-label {
                    font-size: 14px;

                    font-weight: 600;

                    color: #334E68;
                }

                .feedback-stat-label small {
                    display: block;

                    margin-top: 3px;

                    font-size: 11px;

                    font-weight: 700;

                    color: #F25B2A;
                }


                /* =====================================
                   LOADING
                ===================================== */

                .feedback-loading,
                .feedback-error {
                    min-height: 290px;

                    display: flex;

                    align-items: center;
                    justify-content: center;

                    flex-direction: column;

                    gap: 12px;

                    color: #60748A;

                    font-size: 14px;
                }

                .feedback-spinner {
                    color: #1677E8;
                }

                .feedback-error-title {
                    font-size: 18px;

                    font-weight: 700;

                    color: #071C3A;
                }

                .feedback-error-text {
                    font-size: 14px;

                    color: #60748A;
                }

                .feedback-retry-button {
                    border:
                        1.5px solid #1677E8;

                    background: #FFFFFF;

                    color: #1677E8;

                    border-radius: 10px;

                    padding: 9px 17px;

                    font-weight: 700;

                    cursor: pointer;
                }


                /* =====================================
                   TABLET
                ===================================== */

                @media (max-width: 1200px) {

                    .feedback-main-grid {
                        grid-template-columns:
                            0.9fr 0.55fr 1.35fr;

                        gap: 20px;
                    }

                    .feedback-title {
                        font-size: 34px;
                    }

                    .feedback-illustration-circle {
                        width: 145px;
                        height: 145px;
                    }

                    .feedback-chat-card {
                        width: 112px;
                        height: 88px;
                    }
                }


                /* =====================================
                   SMALL TABLET
                ===================================== */

                @media (max-width: 992px) {

                    .feedback-main-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .feedback-left {
                        grid-column: 1;
                    }

                    .feedback-illustration {
                        grid-column: 2;
                    }

                    .feedback-stats {
                        grid-column: 1 / -1;

                        display: grid;

                        grid-template-columns:
                            repeat(3, 1fr);
                    }

                    .feedback-stat {
                        grid-template-columns:
                            45px 1fr;

                        grid-template-rows:
                            auto auto;
                    }

                    .feedback-stat-icon {
                        grid-row: 1 / 3;
                    }

                    .feedback-stat-label {
                        grid-column: 2;
                    }
                }


                /* =====================================
                   MOBILE
                ===================================== */

                @media (max-width: 576px) {

                    .feedback-insight-body {
                        padding: 24px 18px;
                    }

                    .feedback-main-grid {
                        display: flex;

                        flex-direction: column;

                        align-items: stretch;

                        gap: 24px;
                    }

                    .feedback-title {
                        font-size: 30px;
                    }

                    .feedback-description {
                        font-size: 14px;
                    }

                    .feedback-illustration {
                        padding: 5px 0;
                    }

                    .feedback-illustration-circle {
                        width: 145px;
                        height: 145px;
                    }

                    .feedback-stats {
                        display: flex;
                    }

                    .feedback-stat {
                        grid-template-columns:
                            45px 90px 1fr;

                        grid-template-rows: auto;
                    }

                    .feedback-stat-icon {
                        grid-row: auto;
                    }

                    .feedback-stat-label {
                        grid-column: auto;
                    }
                }


                /* =====================================
                   VERY SMALL MOBILE
                ===================================== */

                @media (max-width: 390px) {

                    .feedback-stat {
                        grid-template-columns:
                            42px 82px 1fr;

                        padding: 10px;
                    }

                    .feedback-stat-value {
                        font-size: 23px;
                    }

                    .feedback-stat-label {
                        font-size: 12px;
                    }
                }

                `}
      </style>
    </Container>
  );
};

export default FeedbackInsight;
